/**
 * WebhookSettingsPage - Sprint 9 Enhanced
 * Webhook management UI with simulator, key reveal, and status badges
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  Webhook, Plus, Trash2, RefreshCw, Copy, Eye, EyeOff,
  Check, X, AlertTriangle, Clock, Send, ExternalLink, ChevronDown, ChevronUp,
  Play, Zap, Key, Activity
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  active: { color: 'bg-green-100 text-green-700 border-green-200', icon: Check, label: 'Active' },
  failing: { color: 'bg-yellow-100 text-yellow-700 border-yellow-200', icon: AlertTriangle, label: 'Delivery Failing' },
  disabled: { color: 'bg-red-100 text-red-700 border-red-200', icon: X, label: 'Disabled' }
};

const DELIVERY_STATUS_COLORS = {
  pending: 'bg-blue-100 text-blue-700',
  delivered: 'bg-green-100 text-green-700',
  failed: 'bg-red-100 text-red-700',
  dead_letter: 'bg-gray-100 text-gray-700'
};

const WebhookSettingsPage = () => {
  const [webhooks, setWebhooks] = useState([]);
  const [eventTypes, setEventTypes] = useState([]);
  const [samplePayloads, setSamplePayloads] = useState({});
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showSimulatorModal, setShowSimulatorModal] = useState(false);
  const [selectedWebhook, setSelectedWebhook] = useState(null);
  const [deliveries, setDeliveries] = useState([]);
  const [expandedWebhook, setExpandedWebhook] = useState(null);
  const [webhookDetails, setWebhookDetails] = useState({});
  
  // Create form state
  const [newWebhook, setNewWebhook] = useState({
    name: '',
    url: '',
    events: []
  });
  const [creating, setCreating] = useState(false);
  const [testing, setTesting] = useState(null);
  const [showSecret, setShowSecret] = useState({});
  
  // Simulator state
  const [simulatorConfig, setSimulatorConfig] = useState({
    event_type: 'demand.created',
    webhook_id: null,
    custom_payload: null
  });
  const [simulating, setSimulating] = useState(false);

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('simpliview_token');
    const tenantId = localStorage.getItem('impersonated_tenant_id') || localStorage.getItem('simpliview_user_tenant_id');
    const headers = {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
    if (tenantId) {
      headers['X-Tenant-ID'] = tenantId;
    }
    return headers;
  }, []);

  const fetchWebhooks = useCallback(async () => {
    try {
      const [webhooksRes, eventsRes, simulatorRes] = await Promise.all([
        fetch(`${API_URL}/api/webhooks`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/webhooks/events`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/webhooks/simulate/event-types`, { headers: getAuthHeaders() })
      ]);

      if (webhooksRes.ok) {
        const data = await webhooksRes.json();
        setWebhooks(data.data || []);
      }

      if (eventsRes.ok) {
        const data = await eventsRes.json();
        setEventTypes(data.data || []);
      }

      if (simulatorRes.ok) {
        const data = await simulatorRes.json();
        setSamplePayloads(data.data?.sample_payloads || {});
      }
    } catch (err) {
      console.error('Failed to fetch webhooks:', err);
      toast.error('Failed to load webhooks');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchWebhooks();
  }, [fetchWebhooks]);

  const fetchWebhookDetails = async (webhookId) => {
    try {
      const response = await fetch(`${API_URL}/api/webhooks/${webhookId}`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setWebhookDetails(prev => ({ ...prev, [webhookId]: data.data }));
      }
    } catch (err) {
      console.error('Failed to fetch webhook details:', err);
    }
  };

  const fetchDeliveries = async (webhookId) => {
    try {
      const response = await fetch(`${API_URL}/api/webhooks/${webhookId}/deliveries?limit=20`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setDeliveries(data.data || []);
      }
    } catch (err) {
      console.error('Failed to fetch deliveries:', err);
    }
  };

  const handleCreate = async () => {
    if (!newWebhook.name || !newWebhook.url || newWebhook.events.length === 0) {
      toast.error('Please fill all required fields');
      return;
    }

    setCreating(true);
    try {
      const response = await fetch(`${API_URL}/api/webhooks`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(newWebhook)
      });

      if (response.ok) {
        toast.success('Webhook created successfully');
        setShowCreateModal(false);
        setNewWebhook({ name: '', url: '', events: [] });
        fetchWebhooks();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create webhook');
      }
    } catch (err) {
      toast.error('Failed to create webhook');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (webhookId) => {
    if (!window.confirm('Delete this webhook?')) return;

    try {
      const response = await fetch(`${API_URL}/api/webhooks/${webhookId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        toast.success('Webhook deleted');
        fetchWebhooks();
      } else {
        toast.error('Failed to delete webhook');
      }
    } catch (err) {
      toast.error('Failed to delete webhook');
    }
  };

  const handleTest = async (webhookId) => {
    setTesting(webhookId);
    try {
      const response = await fetch(`${API_URL}/api/webhooks/${webhookId}/test`, {
        method: 'POST',
        headers: getAuthHeaders()
      });

      const result = await response.json();
      if (result.data?.success) {
        toast.success(`Test successful! Status: ${result.data.status_code}`);
      } else {
        toast.error(result.data?.error || 'Test failed');
      }
    } catch (err) {
      toast.error('Test request failed');
    } finally {
      setTesting(null);
    }
  };

  const handleSimulate = async () => {
    setSimulating(true);
    try {
      const response = await fetch(`${API_URL}/api/webhooks/simulate`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(simulatorConfig)
      });

      const result = await response.json();
      if (response.ok) {
        toast.success(`Event simulated! Queued to ${result.data?.queued_to_webhooks || 0} webhook(s)`);
        setShowSimulatorModal(false);
        // Refresh deliveries if viewing a webhook
        if (expandedWebhook) {
          fetchDeliveries(expandedWebhook);
        }
      } else {
        toast.error(result.detail || 'Simulation failed');
      }
    } catch (err) {
      toast.error('Simulation request failed');
    } finally {
      setSimulating(false);
    }
  };

  const handleToggleEnabled = async (webhook) => {
    try {
      const response = await fetch(`${API_URL}/api/webhooks/${webhook.webhook_id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ enabled: !webhook.enabled })
      });

      if (response.ok) {
        toast.success(webhook.enabled ? 'Webhook disabled' : 'Webhook enabled');
        fetchWebhooks();
      }
    } catch (err) {
      toast.error('Failed to update webhook');
    }
  };

  const copyToClipboard = (text, label = 'Copied') => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  const toggleEvents = (event) => {
    setNewWebhook(prev => ({
      ...prev,
      events: prev.events.includes(event)
        ? prev.events.filter(e => e !== event)
        : [...prev.events, event]
    }));
  };

  const toggleSecretVisibility = async (webhookId) => {
    if (!webhookDetails[webhookId]) {
      await fetchWebhookDetails(webhookId);
    }
    setShowSecret(prev => ({ ...prev, [webhookId]: !prev[webhookId] }));
  };

  const StatusBadge = ({ status }) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.active;
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} border flex items-center gap-1`}>
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto" data-testid="webhook-settings">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Webhook className="h-6 w-6" />
            Webhook Settings
          </h1>
          <p className="text-gray-500 mt-1">
            Configure webhooks to receive real-time event notifications
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setShowSimulatorModal(true)}
            className="flex items-center gap-2"
            data-testid="simulate-event-btn"
          >
            <Play className="h-4 w-4" />
            Simulate Event
          </Button>
          <Button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2"
            data-testid="create-webhook-btn"
          >
            <Plus className="h-4 w-4" />
            Create Webhook
          </Button>
        </div>
      </div>

      {/* Webhooks List */}
      {webhooks.length === 0 ? (
        <Card className="text-center py-12">
          <CardContent>
            <Webhook className="h-12 w-12 mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-700">No webhooks configured</h3>
            <p className="text-gray-500 mt-2">
              Create a webhook to receive real-time event notifications
            </p>
            <Button
              onClick={() => setShowCreateModal(true)}
              className="mt-4"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create Your First Webhook
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {webhooks.map((webhook) => (
            <Card key={webhook.webhook_id} className="overflow-hidden">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-lg flex items-center gap-2">
                      {webhook.name}
                      <StatusBadge status={webhook.status} />
                    </CardTitle>
                    <CardDescription className="mt-1 flex items-center gap-2">
                      <ExternalLink className="h-3 w-3" />
                      <code className="text-xs bg-gray-100 px-2 py-1 rounded">
                        {webhook.url}
                      </code>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0"
                        onClick={() => copyToClipboard(webhook.url, 'URL')}
                      >
                        <Copy className="h-3 w-3" />
                      </Button>
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={webhook.enabled}
                      onCheckedChange={() => handleToggleEnabled(webhook)}
                      data-testid={`toggle-${webhook.webhook_id}`}
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleTest(webhook.webhook_id)}
                      disabled={testing !== null}
                      data-testid={`test-${webhook.webhook_id}`}
                    >
                      {testing === webhook.webhook_id ? (
                        <RefreshCw className="h-4 w-4 animate-spin" />
                      ) : (
                        <Send className="h-4 w-4" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-500"
                      onClick={() => handleDelete(webhook.webhook_id)}
                      data-testid={`delete-${webhook.webhook_id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                {/* Events */}
                <div className="flex flex-wrap gap-1 mb-3">
                  {webhook.events?.map((event) => (
                    <Badge key={event} variant="outline" className="text-xs">
                      {event}
                    </Badge>
                  ))}
                </div>

                {/* Signing Key Section */}
                <div className="flex items-center gap-2 mb-3 p-2 bg-gray-50 rounded">
                  <Key className="h-4 w-4 text-gray-400" />
                  <span className="text-xs text-gray-500">Signing Key:</span>
                  <code className="text-xs font-mono flex-1">
                    {showSecret[webhook.webhook_id] && webhookDetails[webhook.webhook_id]
                      ? webhookDetails[webhook.webhook_id].signing_key
                      : '••••••••••••••••••••••••••••••••'}
                  </code>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0"
                    onClick={() => toggleSecretVisibility(webhook.webhook_id)}
                    data-testid={`reveal-key-${webhook.webhook_id}`}
                  >
                    {showSecret[webhook.webhook_id] ? (
                      <EyeOff className="h-3 w-3" />
                    ) : (
                      <Eye className="h-3 w-3" />
                    )}
                  </Button>
                  {showSecret[webhook.webhook_id] && webhookDetails[webhook.webhook_id] && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0"
                      onClick={() => copyToClipboard(webhookDetails[webhook.webhook_id].signing_key, 'Signing key')}
                    >
                      <Copy className="h-3 w-3" />
                    </Button>
                  )}
                </div>
                
                {/* Stats Row */}
                <div className="flex items-center justify-between text-sm text-gray-500">
                  <div className="flex items-center gap-4">
                    <span className="flex items-center gap-1">
                      <Activity className="h-3 w-3" />
                      Failures: <span className={webhook.failure_count > 0 ? 'text-red-500 font-medium' : ''}>{webhook.failure_count || 0}</span>
                    </span>
                    {webhook.last_delivery_at && (
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        Last delivery: {new Date(webhook.last_delivery_at).toLocaleString()}
                      </span>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      if (expandedWebhook === webhook.webhook_id) {
                        setExpandedWebhook(null);
                      } else {
                        setExpandedWebhook(webhook.webhook_id);
                        fetchDeliveries(webhook.webhook_id);
                      }
                    }}
                  >
                    {expandedWebhook === webhook.webhook_id ? (
                      <>
                        <ChevronUp className="h-4 w-4 mr-1" />
                        Hide Logs
                      </>
                    ) : (
                      <>
                        <ChevronDown className="h-4 w-4 mr-1" />
                        View Logs ({webhook.delivery_count || 0})
                      </>
                    )}
                  </Button>
                </div>

                {/* Expanded Delivery Logs */}
                {expandedWebhook === webhook.webhook_id && (
                  <div className="mt-4 border-t pt-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium">Recent Deliveries</h4>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setSimulatorConfig(prev => ({ ...prev, webhook_id: webhook.webhook_id }));
                          setShowSimulatorModal(true);
                        }}
                      >
                        <Zap className="h-3 w-3 mr-1" />
                        Simulate to This Webhook
                      </Button>
                    </div>
                    {deliveries.length === 0 ? (
                      <p className="text-gray-500 text-sm">No deliveries yet</p>
                    ) : (
                      <div className="space-y-2 max-h-64 overflow-y-auto">
                        {deliveries.map((delivery) => (
                          <div
                            key={delivery.delivery_id}
                            className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm"
                          >
                            <div className="flex items-center gap-2">
                              <Badge className={DELIVERY_STATUS_COLORS[delivery.status]}>
                                {delivery.status}
                              </Badge>
                              <span className="font-mono text-xs">{delivery.event_type}</span>
                              {delivery.attempts > 1 && (
                                <span className="text-xs text-gray-400">
                                  (attempt {delivery.attempts})
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-3 text-gray-500">
                              {delivery.response_status && (
                                <span className={`text-xs ${delivery.response_status >= 200 && delivery.response_status < 300 ? 'text-green-600' : 'text-red-600'}`}>
                                  HTTP {delivery.response_status}
                                </span>
                              )}
                              {delivery.error_message && (
                                <span className="text-xs text-red-500 max-w-[200px] truncate" title={delivery.error_message}>
                                  {delivery.error_message}
                                </span>
                              )}
                              <span className="text-xs">
                                {new Date(delivery.created_at).toLocaleString()}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Webhook Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Create Webhook</CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setShowCreateModal(false)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <CardDescription>
                Configure a new webhook endpoint to receive events
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Name *</label>
                <input
                  type="text"
                  value={newWebhook.name}
                  onChange={(e) => setNewWebhook(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="My Webhook"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  data-testid="webhook-name-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">URL *</label>
                <input
                  type="url"
                  value={newWebhook.url}
                  onChange={(e) => setNewWebhook(prev => ({ ...prev, url: e.target.value }))}
                  placeholder="https://api.example.com/webhooks"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  data-testid="webhook-url-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Events *</label>
                <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto p-2 border rounded-lg">
                  {eventTypes.map((event) => (
                    <label
                      key={event}
                      className="flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-50 p-1 rounded"
                    >
                      <input
                        type="checkbox"
                        checked={newWebhook.events.includes(event)}
                        onChange={() => toggleEvents(event)}
                        className="rounded"
                      />
                      <span className="font-mono text-xs">{event}</span>
                    </label>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Selected: {newWebhook.events.length} events
                </p>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setShowCreateModal(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={handleCreate}
                  disabled={creating}
                  data-testid="create-webhook-submit"
                >
                  {creating ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Check className="h-4 w-4 mr-2" />
                      Create Webhook
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Event Simulator Modal */}
      {showSimulatorModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Zap className="h-5 w-5 text-yellow-500" />
                  Simulate Webhook Event
                </CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setShowSimulatorModal(false)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <CardDescription>
                Send a test event through the webhook delivery system
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Event Type *</label>
                <select
                  value={simulatorConfig.event_type}
                  onChange={(e) => setSimulatorConfig(prev => ({ ...prev, event_type: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  data-testid="simulator-event-type"
                >
                  {eventTypes.map((event) => (
                    <option key={event} value={event}>{event}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Target Webhook (optional)</label>
                <select
                  value={simulatorConfig.webhook_id || ''}
                  onChange={(e) => setSimulatorConfig(prev => ({ ...prev, webhook_id: e.target.value || null }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  data-testid="simulator-webhook-target"
                >
                  <option value="">All matching webhooks</option>
                  {webhooks.map((wh) => (
                    <option key={wh.webhook_id} value={wh.webhook_id}>
                      {wh.name} ({wh.url.slice(0, 40)}...)
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Leave empty to send to all webhooks subscribed to this event
                </p>
              </div>

              {/* Sample Payload Preview */}
              {samplePayloads[simulatorConfig.event_type] && (
                <div>
                  <label className="block text-sm font-medium mb-2">Sample Payload Preview</label>
                  <pre className="text-xs bg-gray-100 p-3 rounded-lg overflow-x-auto max-h-40">
                    {JSON.stringify(samplePayloads[simulatorConfig.event_type], null, 2)}
                  </pre>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setShowSimulatorModal(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={handleSimulate}
                  disabled={simulating}
                  className="bg-yellow-500 hover:bg-yellow-600"
                  data-testid="simulate-submit"
                >
                  {simulating ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Simulating...
                    </>
                  ) : (
                    <>
                      <Zap className="h-4 w-4 mr-2" />
                      Simulate Event
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Info Card */}
      <Card className="mt-6 bg-blue-50 border-blue-200">
        <CardContent className="py-4">
          <h4 className="font-medium text-blue-800 mb-2">Webhook Security</h4>
          <p className="text-sm text-blue-700">
            All webhook payloads are signed with HMAC-SHA256. Verify the{' '}
            <code className="bg-blue-100 px-1 rounded">X-ScopeOps-Signature</code> header
            in your endpoint to ensure payload integrity. The signing key is unique per webhook.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default WebhookSettingsPage;
